'use client';

import React, { useMemo } from 'react';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, format, differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { Plus } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
import { CALENDAR_COLOR_MAP, WEEKDAYS_SHORT } from './constants';
export const CalendarMonthView = ({
  currentDate,
  selectedDate,
  events = [],
  onDateSelect,
  onEventClick,
  onAddEvent,
  className,
  slotProps
}) => {
  // Compute calendar days grid (Mon - Sun)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, {
    weekStartsOn: 1
  });
  const calendarEnd = endOfWeek(monthEnd, {
    weekStartsOn: 1
  });
  const days = useMemo(() => {
    return eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd
    });
  }, [calendarStart, calendarEnd]);
  const totalRows = Math.ceil(days.length / 7);

  // Normalize event dates to Date objects
  const parsedEvents = useMemo(() => {
    return events.map(ev => ({
      ...ev,
      startDate: typeof ev.start === 'string' ? new Date(ev.start) : ev.start,
      endDate: typeof ev.end === 'string' ? new Date(ev.end) : ev.end
    }));
  }, [events]);

  // Map of events by day string
  const eventsByDay = useMemo(() => {
    const map = new Map();
    days.forEach(day => {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayEvents = parsedEvents.filter(ev => {
        return ev.startDate <= dayEnd && ev.endDate >= dayStart;
      });
      map.set(format(day, 'yyyy-MM-dd'), dayEvents);
    });
    return map;
  }, [days, parsedEvents]);

  // Selected day events for mobile agenda drawer
  const activeDay = selectedDate || (isSameMonth(currentDate, new Date()) ? new Date() : monthStart);
  const activeDayEvents = eventsByDay.get(format(activeDay, 'yyyy-MM-dd')) || [];
  const monthSlot = slotProps?.monthView;
  return <div {...monthSlot?.root} className={cn('flex flex-1 flex-col bg-white dark:bg-zinc-900', className, monthSlot?.root?.className)}>
            {/* Weekday headers (Mon - Sun) */}
            <div {...monthSlot?.weekdayHeader} className={cn('grid grid-cols-7 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-850/50', monthSlot?.weekdayHeader?.className)}>
                {WEEKDAYS_SHORT.map(wd => <div key={wd} className="py-2.5 text-center text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                        {wd}
                    </div>)}
            </div>

            {/* Month Days Grid */}
            <div {...monthSlot?.grid} className={cn('grid flex-1 grid-cols-7', totalRows === 6 ? 'grid-rows-6' : totalRows === 5 ? 'grid-rows-5' : 'grid-rows-4', monthSlot?.grid?.className)}>
                {days.map((day, idx) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const inMonth = isSameMonth(day, currentDate);
        const isCurrentDay = isToday(day);
        const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
        const dayEvents = eventsByDay.get(dayKey) || [];
        const isLastColumn = (idx + 1) % 7 === 0;
        const isBottomRow = idx >= (totalRows - 1) * 7;
        const visibleEvents = dayEvents.slice(0, 3);
        const overflowCount = Math.max(0, dayEvents.length - 3);
        const cellSlot = monthSlot?.dayCell?.(day, inMonth);
        return <div key={dayKey} onClick={() => onDateSelect?.(day)} {...cellSlot} className={cn('group relative flex flex-col p-1.5 sm:p-2 min-h-24 sm:min-h-28 transition-colors select-none', !inMonth ? 'bg-zinc-50/40 dark:bg-zinc-950/40 text-zinc-400 dark:text-zinc-600' : 'bg-white dark:bg-zinc-900', inMonth && 'hover:bg-zinc-50/80 dark:hover:bg-zinc-850/60 cursor-pointer', !isLastColumn && 'border-r border-zinc-200 dark:border-zinc-800', !isBottomRow && 'border-b border-zinc-200 dark:border-zinc-800', cellSlot?.className)}>
                            {/* Day cell header: Date number & cell hover add button */}
                            <div className="flex items-center justify-between mb-1">
                                <span className={cn('flex size-6 items-center justify-center rounded-full text-xs font-medium', isSelected && 'bg-purple-600 text-white font-semibold shadow-xs', isCurrentDay && !isSelected && 'bg-zinc-200 dark:bg-zinc-700 font-bold text-zinc-900 dark:text-zinc-100', !isSelected && !isCurrentDay && inMonth && 'text-zinc-700 dark:text-zinc-300 font-semibold', !inMonth && 'opacity-60')}>
                                    {format(day, 'd')}
                                </span>

                                {/* Quick Add Event Plus Button on Cell Hover */}
                                {inMonth && <button type="button" onClick={e => {
              e.stopPropagation();
              onAddEvent?.(day);
            }} aria-label={`Add event on ${format(day, 'MMM d')}`} {...monthSlot?.addEventButton?.(day)} className={cn('hidden group-hover:inline-flex size-6 items-center justify-center rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 shadow-2xs transition-colors', monthSlot?.addEventButton?.(day)?.className)}>
                                        <Plus className="size-3.5 stroke-[2.25px]" />
                                    </button>}
                            </div>

                            {/* Events list inside day cell */}
                            <div className="flex flex-col gap-1 overflow-hidden">
                                {visibleEvents.map(ev => {
              const color = ev.color || 'brand';
              const colorStyle = CALENDAR_COLOR_MAP[color] || CALENDAR_COLOR_MAP.brand;
              const isMultiDay = differenceInDays(ev.endDate, ev.startDate) >= 1;
              const isFirstDayOfEvent = isSameDay(day, ev.startDate);
              const pillSlot = monthSlot?.eventPill?.(ev);
              return <div key={ev.id} onClick={e => {
                e.stopPropagation();
                onEventClick?.(ev);
              }} {...pillSlot} className={cn('group/event relative flex items-center justify-between gap-1 rounded-md px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs transition-transform hover:scale-[1.01] cursor-pointer', colorStyle.root, pillSlot?.className)} title={`${ev.title} (${format(ev.startDate, 'h:mm a')})`}>
                                            {/* Mobile collapse dot */}
                                            <span className={cn('inline-block size-1.5 rounded-full md:hidden shrink-0', colorStyle.dot)} />

                                            {/* Desktop content: title and optional time */}
                                            <span className={cn('truncate font-medium max-md:hidden', colorStyle.label)}>{ev.title}</span>

                                            {!isMultiDay && isFirstDayOfEvent && <time className={cn('shrink-0 text-[10px] max-md:hidden opacity-80', colorStyle.time)}>
                                                    {format(ev.startDate, 'h:mm a')}
                                                </time>}
                                        </div>;
            })}

                                {/* Overflow count badge */}
                                {overflowCount > 0 && <div {...monthSlot?.overflowBadge?.(overflowCount)} className={cn('text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 hover:text-purple-600 dark:hover:text-purple-400 pl-0.5 transition-colors', monthSlot?.overflowBadge?.(overflowCount)?.className)}>
                                        +{overflowCount} more...
                                    </div>}
                            </div>
                        </div>;
      })}
            </div>

            {/* Mobile Agenda Drawer for Selected Day (< md) */}
            <div {...monthSlot?.mobileAgenda} className={cn('border-t border-zinc-200 dark:border-zinc-800 p-4 md:hidden bg-zinc-50/50 dark:bg-zinc-850/50', monthSlot?.mobileAgenda?.className)}>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2.5">{format(activeDay, 'EEEE, MMMM d, yyyy')}</h4>
                {activeDayEvents.length === 0 ? <p className="text-xs text-zinc-400 dark:text-zinc-500">No events scheduled for this day.</p> : <div className="flex flex-col gap-1.5">
                        {activeDayEvents.map(ev => {
          const colorStyle = CALENDAR_COLOR_MAP[ev.color || 'brand'];
          return <div key={ev.id} onClick={() => onEventClick?.(ev)} className={cn('flex items-center justify-between rounded-lg p-2.5 text-xs font-medium cursor-pointer', colorStyle.root)}>
                                    <div className="flex items-center gap-2">
                                        <span className={cn('size-2 rounded-full', colorStyle.dot)} />
                                        <span className={cn('font-semibold', colorStyle.label)}>{ev.title}</span>
                                    </div>
                                    <time className={colorStyle.time}>{format(ev.startDate, 'h:mm a')}</time>
                                </div>;
        })}
                    </div>}
            </div>
        </div>;
};